import { Controller } from '@hotwired/stimulus'
import { userTextInputToUrlSlug, getItemSearchPath } from 'lib/linkHelpers'
import { googleAnalytics3Event } from 'lib/googleAnalytics'

export default class extends Controller {
  static targets = [
    'input',
    'headerLabel',
    'headerOptions',
    'itemTypeSelector',
    'autocompleteResults',
    'autocompleteButton',
    'popularSearchTermsForItemType'
  ]

  static values = {
    itemType: String,
    autocomplete: Object,
    autocompleteIndex: Number,
    hasAutocompleteResults: Boolean,
    showClearButton: Boolean
  }

  static classes = [
    'expandedItemTypeSelector',
    'hiddenPopularSearchTerms'
  ]

  connect () {
    this.cachedAutocompleteData = {}
    this.setHeaderLabel()
    this.setPopularSearchTerms()
  }

  setHeaderLabel () {
    if (this.hasHeaderLabelTarget) {
      const searchItemType = this.itemTypeValue
      const currentElement = this.headerOptionsTarget.querySelector('.new-search-form__item-type-option--current')
      if (currentElement) {
        currentElement.classList.remove('new-search-form__item-type-option--current')
      }
      const newElementToHighlight = this.headerOptionsTarget.querySelector(`[data-type="${searchItemType}"]`)
      if (newElementToHighlight) {
        newElementToHighlight.classList.add('new-search-form__item-type-option--current')
        this.headerLabelTarget.innerText = newElementToHighlight.innerText
      }
    }
  }

  setPopularSearchTerms () {
    const searchItemType = this.itemTypeValue
    const popularSearchTermsForItemTypeTargets = this.popularSearchTermsForItemTypeTargets

    if (popularSearchTermsForItemTypeTargets && Array.isArray(popularSearchTermsForItemTypeTargets)) {
      popularSearchTermsForItemTypeTargets.forEach((popularSearchTermForItemType) => {
        if (searchItemType === popularSearchTermForItemType.dataset.itemType) {
          popularSearchTermForItemType.classList.remove(this.hiddenPopularSearchTermsClass)
        } else {
          popularSearchTermForItemType.classList.add(this.hiddenPopularSearchTermsClass)
        }
      })
    }
  }

  expandItemTypeSelector (event) {
    if (this.hasItemTypeSelectorTarget) {
      this.itemTypeSelectorTarget.classList.add(this.expandedItemTypeSelectorClass)
    }
  }

  collapseItemTypeSelector (event) {
    if (this.hasItemTypeSelectorTarget) {
      this.itemTypeSelectorTarget.classList.remove(this.expandedItemTypeSelectorClass)
    }
  }

  itemTypeChange (event) {
    event.preventDefault()
    event.stopImmediatePropagation()
    const newItemType = event.target.getAttribute('data-type')
    this.itemTypeValue = newItemType
    this.inputTarget.value = ''
    this.setHeaderLabel()
    this.setPopularSearchTerms()
    this.inputTarget.focus()
    this.collapseItemTypeSelector()
    return false
  }

  logFreeTextSearchGaEvent (searchTerm, callback) {
    googleAnalytics3Event({
      eventAction: searchTerm,
      eventCategory: 'Search Query (free text)',
      eventLabel: this.itemTypeValue,
      callback: callback
    })
  }

  logAutocompleteSearchGaEvent (searchTerm, callback) {
    googleAnalytics3Event({
      eventAction: searchTerm,
      eventCategory: 'Search Query (autocomplete)',
      eventLabel: this.itemTypeValue,
      callback: callback
    })
  }

  async search (event) {
    event.preventDefault()
    if (this.isAutoCompleteItemType()) {
      // User is submitting the form on an autocomplete item type
      const searchMatches = await this.findAutoCompleteMatches()
      if (searchMatches.length > 0 && typeof searchMatches[this.autocompleteIndexValue] !== 'undefined') {
        // User has pressed enter with an auto complete search match highligted, we're going to search for that.
        this.redirectToAutoCompleteResult(searchMatches[this.autocompleteIndexValue].label, searchMatches[this.autocompleteIndexValue].url)
        return
      } else {
        // No matches found, so we're doing a normal search without auto completed option available.
        // This bubbles down to the normal getItemSearchPath() below
      }
    }

    const searchItemType = this.itemTypeValue
    const searchUrlSlug = userTextInputToUrlSlug(this.inputTarget.value)
    if (searchUrlSlug.length > 0) {
      const destinationUrl = getItemSearchPath({ itemType: searchItemType, searchTermUrlSlug: searchUrlSlug })
      this.logFreeTextSearchGaEvent(this.inputTarget.value, () => {
        window.location.assign(destinationUrl)
      })
    }
    return false
  }

  clearSearchTerms () {
    this.inputTarget.value = ''
    this.showClearButtonValue = false
  }

  isAutoCompleteItemType () {
    return typeof this.autocompleteValue[this.itemTypeValue] !== 'undefined'
  }

  async handleAutocomplete (event) {
    let keyCode

    if (event.key !== undefined) {
      keyCode = event.key
    } else if (event.keyIdentifier !== undefined) {
      keyCode = event.keyIdentifier
    } else if (event.keyCode !== undefined) {
      keyCode = event.keyCode
    }

    if (this.isAutoCompleteItemType()) {
      const searchMatches = await this.findAutoCompleteMatches()

      // For auto complete item types, we want to capture when user presses up or down, change which option is highlighted
      if (keyCode === 40 || keyCode === 38 || keyCode === 'ArrowDown' || keyCode === 'ArrowUp') {
        event.preventDefault()
        if (keyCode === 40 || keyCode === 'ArrowDown') {
          // User pressed down arrow.
          if (this.autocompleteIndexValue < searchMatches.length - 1) {
            this.autocompleteIndexValue++
          }
        }
        if (keyCode === 38 || keyCode === 'ArrowUp') {
          // User pressed up arrow.
          if (this.autocompleteIndexValue > 0) {
            this.autocompleteIndexValue--
          }
        }
      } else if (keyCode === 13 || keyCode === 'Enter') {
        // User pressed enter, this is handled by a normal <form> submit above
      } else {
        // Another type of keypress, we reset our autocomplete index to 0 & reset the autocomplete results HTML
        this.autocompleteIndexValue = 0
        this.autocompleteResultsTarget.innerHTML = ''
      }

      // Add data attribute to dom so we can hide/show the results dropdown nicely
      this.hasAutocompleteResultsValue = searchMatches.length > 0

      // Now render our search results on the page
      searchMatches.forEach((result, index) => {
        const existingAutocompleteResultButton = document.querySelector(`[data-url='${result.url}']`)

        if (existingAutocompleteResultButton) {
          // This search match is already rendered, so just update the class
          if (index === this.autocompleteIndexValue) {
            existingAutocompleteResultButton.classList.add('new-search-form__autocomplete-result--current')
          } else {
            existingAutocompleteResultButton.classList.remove('new-search-form__autocomplete-result--current')
          }
        } else {
          // This search match isn't yet rendered, so construct from the template
          const autocompleteResultButton = this.autocompleteButtonTarget.content.children[0].cloneNode()
          autocompleteResultButton.innerHTML = `${result.label.slice(0, result.resultIndexStart)}<strong>${result.label.slice(result.resultIndexStart, result.resultIndexEnd)}</strong>${result.label.slice(result.resultIndexEnd)}`
          autocompleteResultButton.dataset.label = result.label
          autocompleteResultButton.dataset.url = result.url
          autocompleteResultButton.dataset.index = index
          if (index === this.autocompleteIndexValue) {
            autocompleteResultButton.classList.add('new-search-form__autocomplete-result--current')
          }
          this.autocompleteResultsTarget.appendChild(autocompleteResultButton)
        }
      })
    }
  }

  async getAutocompleteData (itemType) {
    if (typeof this.cachedAutocompleteData[itemType] !== 'undefined') {
      // We're already cached! return it
      return this.cachedAutocompleteData[itemType]
    }
    // Not cached yet, fetch + cache + return.
    const response = await fetch(this.autocompleteValue[itemType])
    const data = await response.json()
    this.cachedAutocompleteData[itemType] = data
    return this.cachedAutocompleteData[itemType]
  }

  async findAutoCompleteMatches () {
    if (this.inputTarget.value.length === 0) return []

    const dataSource = await this.getAutocompleteData(this.itemTypeValue)
    const searchQueryBits = userTextInputToUrlSlug(this.inputTarget.value).split('-')
    return dataSource.reduce((acc, item) => {
      let weFoundAPartialMatch = false // This is a flag to figure out if the current search word has matched or not
      searchQueryBits.forEach(searchQueryBit => {
        if (!weFoundAPartialMatch) {
          const resultIndex = item.label.toLowerCase().indexOf(searchQueryBit)
          if (resultIndex !== -1) {
            item.resultIndexStart = resultIndex
            item.resultIndexEnd = resultIndex + searchQueryBit.length
            acc.push(item)
            weFoundAPartialMatch = true
          }
        }
      })
      return acc
    }, [])
      .sort((a, b) => a.label.length - b.label.length)
      .slice(0, 10)
  }

  clickAutocompleteResult (event) {
    event.preventDefault()
    this.redirectToAutoCompleteResult(event.currentTarget.dataset.label, event.currentTarget.dataset.url)
    return false
  }

  redirectToAutoCompleteResult (label, url) {
    this.logAutocompleteSearchGaEvent(label, () => {
      window.location.assign(url)
    })
  }

  hoverAutocompleteResult (event) {
    this.autocompleteIndexValue = event.target.dataset.index

    for (const result of this.autocompleteResultsTarget.children) {
      result.classList.remove('new-search-form__autocomplete-result--current')
    }

    event.target.classList.add('new-search-form__autocomplete-result--current')
  }
}
